import React, { useState, useMemo } from 'react'
import GenericSelectionModal from 'src/components/common/select-component/GenericSelectionModal'
import { DynamicSegmentFilterErrorType, DynamicSegmentFilterType, DynamicSegmentFieldType, DynamicSegmentOperatorInfoType } from 'src/lib/types/segment'
import DatePicker from 'src/components/common/calendar/DatePicker'
import { addDays, format } from 'date-fns'
import { Popover, PopoverContent, PopoverTrigger, Button, Tooltip, TooltipTrigger, TooltipContent, Input } from '@shopflo/ui'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import type { TagInputVariant } from 'src/components/segments/TagInput'
import { isEventMetricCountField } from 'src/lib/utils/segmentsHelper'
import TagInput from 'src/components/segments/TagInput'
import { PILL_ERROR_CLASS, PILL_INTERACTIVE_CLASS, TAG_PILL_CLASS } from '../condition-classes'

function getTagInputConfig(field: DynamicSegmentFieldType): { variant: TagInputVariant; placeholderKey: 'pincode' | 'phone' | 'string' } {
  const id = (field.id || '').toLowerCase()
  const ref = (field.columnReference || '').toLowerCase()
  const combined = `${id} ${ref}`
  if (combined.includes('pincode')) return { variant: 'number', placeholderKey: 'pincode' }
  if (combined.includes('phone')) return { variant: 'number', placeholderKey: 'phone' }
  return { variant: 'string', placeholderKey: 'string' }
}

interface FilterValueBuilderProps {
  filter: DynamicSegmentFilterType
  setFilter: (filter: DynamicSegmentFilterType) => void
  /** When true, unit labels like "days" are rendered inside. When false, layout handles them (e.g. "times", "days") */
  renderUnitLabel?: boolean
  /** Non-interactive display (manager segment view) */
  readOnly?: boolean
}

function findFormFilterErrorById(filters: DynamicSegmentFilterErrorType[], filterId: string): DynamicSegmentFilterErrorType | null {
  for (const filterErr of filters) {
    if (filterErr.id === filterId) {
      return filterErr
    }
    if (filterErr.children.length > 0) {
      const childError = findFormFilterErrorById(filterErr.children, filterId)
      if (childError) return childError
    }
  }
  return null
}

export const FilterValueBuilder: React.FC<FilterValueBuilderProps> = ({
  filter,
  setFilter,
  renderUnitLabel = true,
  readOnly = false
}) => {
  const { t } = useLocale()
  const { formErrorState, actions } = useDynamicSegment()
  const [stringInputOpen, setStringInputOpen] = useState(false)
  const [pillInputValue, setPillInputValue] = useState('')

  const handleStringOpenChange = (open: boolean) => {
    if (open) setPillInputValue(filter.values.join('\n'))
    setStringInputOpen(open)
  }

  const minDate = addDays(new Date(), -90)

  // Find error for this filter
  const filterError = useMemo(
    () => findFormFilterErrorById(formErrorState.filters, filter.id),
    [formErrorState.filters, filter.id]
  )

  // Clear error for this filter
  const clearFilterError = () => {
    if (!filterError?.values.status) return

    const clearError = (filters: typeof formErrorState.filters): typeof formErrorState.filters => {
      return filters.map(filterErr => {
        if (filterErr.id === filter.id) {
          return { ...filterErr, values: { status: false, message: '' } }
        }
        if (filterErr.children.length > 0) {
          return { ...filterErr, children: clearError(filterErr.children) }
        }
        return filterErr
      })
    }

    actions.setFormErrorState({ ...formErrorState, filters: clearError(formErrorState.filters) })
  }

  const updateFilterValues = (newValues: string[]) => {
    setFilter({ ...filter, values: newValues })
    clearFilterError()
  }

  const applyStringValues = (raw: string) => {
    const values = raw.split('\n').map(v => v.trim()).filter(Boolean)
    updateFilterValues(values)
    setStringInputOpen(false)
  }

  const handleOperatorChange = (operatorKey: string) => {
    const newOperator = filter.field.operators.find(op => op.key === operatorKey)
    if (newOperator) {
      const defaultTimes =
        isEventMetricCountField(filter.field) &&
        newOperator.dataType === 'NUMBER' &&
        newOperator.valueCount === 1
          ? ['1']
          : []
      setFilter({ ...filter, operator: newOperator, values: defaultTimes })
    }
  }


  const hasValuesError = Boolean(filterError?.values.status)

  const renderValueInput = () => {
    const { dataType, valueCount } = filter.operator
    const valuePillClass = hasValuesError ? PILL_ERROR_CLASS : PILL_INTERACTIVE_CLASS

    // Rule 0: If field has allowedValues, show GenericSelectionModal
    if (filter.field.allowedValues && filter.field.allowedValues.length > 0) {
      const selectValuePill = (
        <div className={`${valuePillClass} w-fit`}>
          {filter.values[0] || t('segment_creation.select_value')}{hasValuesError ? '?' : ''}
        </div>
      )
      return (
        <GenericSelectionModal
          options={filter.field.allowedValues.map(value => ({ key: value, value: value }))}
          selectedKey={filter.values[0] || ''}
          onSelect={option => updateFilterValues([option.key])}
          placeholderComponent={
            hasValuesError ? (
              <Tooltip>
                <TooltipTrigger asChild>{selectValuePill}</TooltipTrigger>
                <TooltipContent>{filterError?.values.message}</TooltipContent>
              </Tooltip>
            ) : (
              selectValuePill
            )
          }
          showSearch={filter.field.allowedValues.length > 10}
          triggerClassName='!border-0 !outline-none'
          contentClassName='w-48'
        />
      )
    }

    // Rule 1: BOOLEAN - no value input
    if (dataType === 'BOOLEAN') {
      return null
    }

    // Rule 2: STRING - InlinePillInput inside dropdown (number/string variant by field); input always active when dropdown opens
    if (dataType === 'STRING') {
      const valuesCount = filter.values.length
      const valuesPart =
        valuesCount > 3
          ? filter.values.slice(0, 3).map(v => (v.length > 8 ? `${v.slice(0, 8)}…` : v)).join(', ')
          : filter.values.map(v => (v.length > 8 ? `${v.slice(0, 8)}…` : v)).join(', ')
      const morePart = valuesCount > 3 ? `+${valuesCount - 3} more` : null

      const triggerButton = (
        <button
          className={`${valuePillClass} w-fit max-w-full min-w-0 shrink flex items-center gap-1 overflow-hidden`}
          onClick={() => handleStringOpenChange(true)}
        >
          {valuesCount === 0 ? (
            <span>{hasValuesError ? 'any?' : 'any'}</span>
          ) : (
            <>
              <span className='truncate min-w-0 flex-1'>{valuesPart}</span>
              {morePart && <span className='shrink-0'>{morePart}</span>}
            </>
          )}
        </button>
      )

      const wrappedTrigger =
        hasValuesError || valuesCount > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>
              {hasValuesError ? filterError?.values.message : filter.values.join(', ')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        )

      const tagInputConfig = getTagInputConfig(filter.field)
      return (
        <Popover open={stringInputOpen} onOpenChange={handleStringOpenChange}>
          {wrappedTrigger}
          <PopoverContent
            className='w-72 p-1.5 flex flex-col bg-white rounded-xl'
            align='start'
            style={{ boxShadow: '0px 0px 0px 0.5px rgba(9,9,11,0.12), 0px 8px 16px -4px rgba(9,9,11,0.12)' }}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
            onKeyDownCapture={e => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              e.stopPropagation()
              applyStringValues(pillInputValue)
            }}
          >
            <TagInput
              value={pillInputValue}
              onChange={setPillInputValue}
              variant={tagInputConfig.variant}
              placeholder={t(`segment_creation.tag_input_placeholder_${tagInputConfig.placeholderKey}`)}
              className='min-h-[128px]'
              autoFocus
            />
            <Button variant='outline' size='sm' className='w-full mt-1.5' onClick={() => applyStringValues(pillInputValue)}>
              {t('segment_creation.add_label')}
            </Button>
          </PopoverContent>
        </Popover>
      )
    }

    // Rule 3 & 4: NUMBER
    if (dataType === 'NUMBER') {
      const isTimesCount = isEventMetricCountField(filter.field) && valueCount === 1
      const numberInputProps = {
        type: 'number' as const,
        variant: 'filled' as const,
        dimension: 'sm' as const,
        placeholder: isTimesCount ? '1' : '0',
        error: hasValuesError,
        className: 'w-12 text-center shrink-0 !text-text-sm-medium text-zinc-600'
      }
      if (valueCount === 1) {
        return (
          <>
            <Input
              {...numberInputProps}
              value={filter.values[0] || ''}
              onChange={e => updateFilterValues([e.target.value])}
            />
            {filter.field.dataType === 'DATE' && renderUnitLabel && (
              <span className='text-text-sm-medium text-zinc-500 shrink-0'>{t('segment_creation.days_label')}</span>
            )}
          </>
        )
      } else if (valueCount === 2) {
        return (
          <div className='flex flex-nowrap items-center gap-1 shrink-0'>
            <Input
              {...numberInputProps}
              value={filter.values[0] || ''}
              onChange={e => updateFilterValues([e.target.value, filter.values[1] || ''])}
            />
            <span className='text-text-sm-regular text-zinc-400'>–</span>
            <Input
              {...numberInputProps}
              value={filter.values[1] || ''}
              onChange={e => updateFilterValues([filter.values[0] || '', e.target.value])}
            />
          </div>
        )
      }
    }

    // Rule 5 & 6: DATE
    if (dataType === 'DATE') {
      const dateTriggerSpan = (val: string | undefined, label: string) => (
        <span className={`${valuePillClass} w-fit`}>
          {val ? format(new Date(val), 'dd MMM yyyy') : label}{hasValuesError && !val ? '?' : ''}
        </span>
      )
      if (valueCount === 1) {
        const trigger = dateTriggerSpan(filter.values[0], t('segment_creation.select_date'))
        return (
          <DatePicker
            date={filter.values[0] ? new Date(filter.values[0]) : new Date()}
            onChange={date => updateFilterValues([date.toISOString()])}
            disableFutureDates={true}
            min={minDate}
            trigger={
              hasValuesError ? (
                <Tooltip>
                  <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                  <TooltipContent>{filterError?.values.message}</TooltipContent>
                </Tooltip>
              ) : (
                trigger
              )
            }
          />
        )
      } else if (valueCount === 2) {
        const trigger1 = dateTriggerSpan(filter.values[0], t('segment_creation.select_date'))
        const trigger2 = dateTriggerSpan(filter.values[1], t('segment_creation.select_date'))
        const wrapWithTooltip = (node: React.ReactNode) =>
          hasValuesError ? (
            <Tooltip>
              <TooltipTrigger asChild>{node}</TooltipTrigger>
              <TooltipContent>{filterError?.values.message}</TooltipContent>
            </Tooltip>
          ) : (
            node
          )
        return (
          <div className='flex flex-nowrap items-center gap-1 shrink-0'>
            <DatePicker
              date={filter.values[0] ? new Date(filter.values[0]) : new Date()}
              onChange={date => updateFilterValues([date.toISOString(), filter.values[1] || ''])}
              disableFutureDates={true}
              min={minDate}
              trigger={wrapWithTooltip(trigger1)}
            />
            <span className='text-text-sm-regular text-zinc-400'>–</span>
            <DatePicker
              date={filter.values[1] ? new Date(filter.values[1]) : new Date()}
              onChange={date => updateFilterValues([filter.values[0] || '', date.toISOString()])}
              disableFutureDates={true}
              min={minDate}
              trigger={wrapWithTooltip(trigger2)}
            />
          </div>
        )
      }
    }

    return null
  }

  const renderReadOnlyValue = () => {
    const { dataType, valueCount } = filter.operator

    if (filter.field.allowedValues && filter.field.allowedValues.length > 0) {
      return <div className={`${TAG_PILL_CLASS} w-fit`}>{filter.values[0] || '—'}</div>
    }
    if (dataType === 'BOOLEAN') {
      return null
    }
    if (dataType === 'STRING') {
      if (filter.values.length === 0) {
        return <div className={`${TAG_PILL_CLASS} w-fit`}>any</div>
      }
      return <div className={`${TAG_PILL_CLASS} max-w-full min-w-0 break-words`}>{filter.values.join(', ')}</div>
    }
    if (dataType === 'NUMBER') {
      if (valueCount === 1) {
        return (
          <>
            <span className={TAG_PILL_CLASS}>{filter.values[0] ?? '—'}</span>
            {filter.field.dataType === 'DATE' && renderUnitLabel && (
              <span className='text-text-sm-medium text-zinc-500 shrink-0'>{t('segment_creation.days_label')}</span>
            )}
          </>
        )
      }
      if (valueCount === 2) {
        return (
          <div className='flex flex-nowrap items-center gap-1 shrink-0'>
            <span className={TAG_PILL_CLASS}>{filter.values[0] ?? '—'}</span>
            <span className='text-text-sm-regular text-zinc-400'>–</span>
            <span className={TAG_PILL_CLASS}>{filter.values[1] ?? '—'}</span>
          </div>
        )
      }
    }
    if (dataType === 'DATE') {
      if (valueCount === 1) {
        return (
          <span className={`${TAG_PILL_CLASS} w-fit`}>
            {filter.values[0] ? format(new Date(filter.values[0]), 'dd MMM yyyy') : '—'}
          </span>
        )
      }
      if (valueCount === 2) {
        return (
          <div className='flex flex-nowrap items-center gap-1 shrink-0'>
            <span className={TAG_PILL_CLASS}>
              {filter.values[0] ? format(new Date(filter.values[0]), 'dd MMM yyyy') : '—'}
            </span>
            <span className='text-text-sm-regular text-zinc-400'>–</span>
            <span className={TAG_PILL_CLASS}>
              {filter.values[1] ? format(new Date(filter.values[1]), 'dd MMM yyyy') : '—'}
            </span>
          </div>
        )
      }
    }
    return null
  }

  if (readOnly) {
    return (
      <div className='flex w-fit min-w-0 flex-row flex-wrap items-center gap-1'>
        <div className={TAG_PILL_CLASS}>{filter.operator.label}</div>
        <div className='flex min-w-0 flex-wrap items-center justify-start gap-1'>
          {renderReadOnlyValue()}
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-row items-center gap-1 w-fit min-w-0'>
      {/* Operator Selector */}
      <GenericSelectionModal
        options={filter.field.operators.map(operator => ({ key: operator.key, value: operator.label }))}
        selectedKey={filter.operator.key}
        onSelect={option => handleOperatorChange(option.key)}
        placeholderComponent={
          <div className={PILL_INTERACTIVE_CLASS}>
            {filter.operator.label}
          </div>
        }
        showSearch={false}
        triggerClassName='!border-0 !outline-none'
        contentClassName='w-48'
      />

      {/* Value Input(s) - w-fit, expands with content up to remaining width. overflow-visible so error/focus states aren't clipped */}
      <div className='flex-1 min-w-0 flex items-center justify-start overflow-visible'>
        {renderValueInput()}
      </div>
    </div>
  )
}
